'use client';

import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, FormEvent } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import faqs from './FAQ.json';
import WhatsAppLink from '@/components/common/WhatsAppLink';
import { CONTACT, PHONE_LINES, waUrl } from '@/lib/contact';
import { TRADE_TERMS } from '@/lib/commercialTerms';
import { track } from '@/lib/analytics';
import { classifyQuery, detectIntent, isFollowUp, looksNonEnglish, FAQItem } from './faqSearch';

// ---------------------------------------------------------------------------
// Types  - every message is JSON-serialisable so the thread can persist across
// page navigations (B2B buyers browse products mid-conversation).
// ---------------------------------------------------------------------------

type Role = 'user' | 'agent' | 'system';

interface BaseMsg {
  id: number;
  ts: number;
}
interface TextMsg extends BaseMsg {
  kind: 'text';
  role: Role;
  content: string;
  /** Whether this is an answer the visitor can rate, and their rating if given. */
  feedback?: boolean;
  rating?: 'up' | 'down';
}
interface SuggestionsMsg extends BaseMsg {
  kind: 'suggestions';
  items: FAQItem[];
  /** Append a "None of these  - talk to sales" escape hatch (audit H6). */
  escape?: boolean;
}
interface ContactMsg extends BaseMsg {
  kind: 'contact';
}
interface ActionLink {
  label: string;
  href: string;
  icon: string;
}
interface ActionsMsg extends BaseMsg {
  kind: 'actions';
  items: ActionLink[];
}
type Message = TextMsg | SuggestionsMsg | ContactMsg | ActionsMsg;

interface ChatProps {
  onClose: () => void;
}

interface Topic {
  label: string;
  icon: string;
  seed: string; // query used to surface the most relevant FAQs
}

// ---------------------------------------------------------------------------
// Static config
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'floradine.chat.v1';
// Expire an idle conversation so a returning visitor starts clean rather than
// on top of a stale thread (audit L3). Stored in sessionStorage, so it also
// clears when the browser tab closes; the TTL guards a tab left open for hours.
const HISTORY_TTL_MS = 4 * 60 * 60 * 1000;
const ENQUIRY_URL = '/products#inquiry';
const CONTACT_URL = '/contact';
// Discharge ports, read from the master commercial terms so the chat never
// drifts from the Products / Shipping copy.
const CONTACT_UK_PORTS = TRADE_TERMS.ukPorts.join(', ');
const CONTACT_EU_PORTS = TRADE_TERMS.euPorts.join(', ');
const seedFaqs = faqs as FAQItem[];

const TOPICS: Topic[] = [
  { label: 'Talk to Sales', icon: 'ChatBubbleLeftRightIcon', seed: '__contact__' },
  {
    label: 'Products & Materials',
    icon: 'CubeIcon',
    seed: 'bagasse areca products materials types',
  },
  {
    label: 'Pricing & MOQ',
    icon: 'TagIcon',
    seed: 'pricing price moq minimum order quantity payment terms',
  },
  {
    label: 'Samples & Custom',
    icon: 'SwatchIcon',
    seed: 'free samples customisation logo private label branding',
  },
  {
    label: 'Shipping & Logistics',
    icon: 'TruckIcon',
    seed: 'shipping freight container delivery ports incoterms lead time',
  },
  {
    label: 'Compliance & Certs',
    icon: 'ShieldCheckIcon',
    seed: 'certification compostable en13432 fda eu compliance phytosanitary',
  },
];

const GREETING =
  "Hi 👋 I'm Flora Dine's automated assistant. I can answer questions about our eco-tableware range, pricing, MOQs, samples, shipping and export compliance in seconds. For anything I can't cover, our export team replies within 24–48 hours on business days. What would you like to know?";

const formatTime = (ts: number) =>
  new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Wrapped so the timestamp is read at call time inside handlers (kept out of
// render, where the purity rules forbid impure globals like Date.now).
const now = (): number => new Date().getTime();

const freshThread = (): Message[] => [
  { id: 1, kind: 'text', role: 'system', content: GREETING, ts: now() },
];

// Hydrate from storage on mount. Safe to read synchronously: <Chat> only ever
// mounts client-side (it's rendered behind the launcher's open state), so there
// is no server render to mismatch against.
//
// Persisted in sessionStorage so an in-progress chat survives page navigations
// and reloads (B2B buyers browse products mid-conversation) but does not linger
// across sessions. A time-to-live guards the case of a tab left open for hours,
// so nobody resumes onto a stale thread with an old timestamp (audit L3).
const loadThread = (): Message[] => {
  if (typeof window === 'undefined') return freshThread();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Message[];
      if (Array.isArray(saved) && saved.length > 0) {
        const last = saved.at(-1);
        if (last?.ts && Date.now() - last.ts < HISTORY_TTL_MS) return saved;
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch {
    /* corrupt storage  - fall back to a fresh thread */
  }
  return freshThread();
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Chat: React.FC<ChatProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>(loadThread);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showTopics, setShowTopics] = useState(() => messages.length <= 1);
  const [confirmReset, setConfirmReset] = useState(false);
  // FAQs default to the bundled seed and upgrade to the live CMS list on mount.
  const [faqList, setFaqList] = useState<FAQItem[]>(seedFaqs);

  // ── Load FAQ knowledge base (Supabase-backed) ─────────────────────────────
  useEffect(() => {
    let active = true;
    fetch('/api/faq')
      .then((r) => r.json())
      .then((d) => {
        if (active && Array.isArray(d?.faqs) && d.faqs.length) setFaqList(d.faqs as FAQItem[]);
      })
      .catch(() => {
        /* keep the seed FAQs */
      });
    return () => {
      active = false;
    };
  }, []);

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const idRef = useRef(messages.reduce((max, m) => Math.max(max, m.id), 1) + 1);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // The last question we searched on, so follow-ups like "and what about
  // areca?" continue the topic rather than starting cold (audit H3).
  const lastQueryRef = useRef('');

  const genId = () => idRef.current++;

  // ── Persistence ─────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [messages]);

  // ── Cleanup any in-flight reply timers on unmount ─────────────────────────
  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
    },
    []
  );

  // ── Scroll + focus management ─────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    const el = scrollAreaRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  useLayoutEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ── Message helpers ───────────────────────────────────────────────────────
  const append = (msg: Message) => setMessages((prev) => [...prev, msg]);

  const respond = (build: () => Message[], delay = 650) => {
    setIsTyping(true);
    const t = setTimeout(() => {
      setMessages((prev) => [...prev, ...build()]);
      setIsTyping(false);
    }, delay);
    timers.current.push(t);
  };

  // Small factories so the intent branches below read as a list of messages.
  // `feedback` marks a substantive answer the visitor can rate (audit L2); small
  // talk, hand-off leads and the fallback are left unrated.
  const textMsg = (content: string, feedback = false): TextMsg => ({
    id: genId(),
    kind: 'text',
    role: 'agent',
    content,
    ...(feedback ? { feedback: true } : {}),
    ts: now(),
  });
  const contactMsg = (): ContactMsg => ({ id: genId(), kind: 'contact', ts: now() });
  const actionsMsg = (items: ActionLink[]): ActionsMsg => ({
    id: genId(),
    kind: 'actions',
    items,
    ts: now(),
  });

  const sendContactCard = (lead?: string) => {
    respond(() => {
      const out: Message[] = [];
      if (lead) out.push(textMsg(lead));
      out.push(contactMsg());
      return out;
    });
  };

  // Links surfaced by the pricing / off-route intents (audit M1, suggestion 16).
  // A factory, not a value: it mints message ids, which must happen when the
  // reply is sent, never during render.
  const enquiryAction = (): ActionsMsg =>
    actionsMsg([
      { label: 'Send an enquiry', href: ENQUIRY_URL, icon: 'DocumentTextIcon' },
      { label: 'Contact page', href: CONTACT_URL, icon: 'EnvelopeIcon' },
    ]);

  const answerQuery = (rawText: string) => {
    // Continue the previous topic for follow-ups like "and what about areca?"
    // by searching the last question together with the new words (audit H3).
    const query =
      isFollowUp(rawText) && lastQueryRef.current ? `${lastQueryRef.current} ${rawText}` : rawText;

    const intent = detectIntent(rawText);

    // ── Hard intents: small talk and human hand-off win over the FAQ index ──
    if (intent === 'greeting') {
      setShowTopics(true);
      respond(() => [
        textMsg(
          "Hi there! 👋 I'm Flora Dine's automated assistant. Ask me about our eco-tableware range, pricing, MOQs, samples, shipping or export compliance - or pick a topic below."
        ),
      ]);
      return;
    }
    if (intent === 'thanks') {
      respond(() => [
        textMsg(
          "You're welcome! 🌱 Anything else I can help with - pricing, samples, shipping or compliance?"
        ),
      ]);
      return;
    }
    if (intent === 'human') {
      sendContactCard(
        "Of course - I'll point you to our export & sales team. They reply within 24–48 hours on business days, and you can reach them on any of these channels:"
      );
      return;
    }

    const { tier, results } = classifyQuery(query, faqList);

    // ── Confident FAQ answer, plus any genuinely-related reading ────────────
    if (tier === 'answer') {
      lastQueryRef.current = query;
      const top = results[0];
      const related = results
        .slice(1)
        .filter((r) => r.score >= top.score * 0.35 && (r.titleHits >= 1 || r.coverage >= 0.5))
        .slice(0, 2)
        .map((r) => r.item);
      respond(() => {
        const out: Message[] = [textMsg(top.item.answer, true)];
        if (related.length)
          out.push({ id: genId(), kind: 'suggestions', items: related, ts: now() });
        return out;
      });
      return;
    }

    // ── Soft intents: the FAQ wasn't confident, so serve a purposeful reply ──
    if (intent === 'pricing') {
      respond(() => [
        textMsg(
          "We don't publish unit prices - every quote is built around your specification, quantity and Incoterm, and most come back within 24–48 hours. Share your requirement and we'll price it up:",
          true
        ),
        enquiryAction(),
        contactMsg(),
      ]);
      return;
    }
    if (intent === 'ship-elsewhere') {
      respond(() => [
        textMsg(
          `Our published sea-freight routes are to the UK (${CONTACT_UK_PORTS}) and the EU (${CONTACT_EU_PORTS}), but we export beyond them too. Tell our team your destination and we'll quote routing, Incoterms and lead time:`,
          true
        ),
        enquiryAction(),
        contactMsg(),
      ]);
      return;
    }
    if (intent === 'location') {
      respond(() => [
        textMsg(
          `${CONTACT.legalEntity} is based at ${CONTACT.addressLine1}, ${CONTACT.addressLine2}. Office hours are ${CONTACT.officeHours.weekdays}; ${CONTACT.officeHours.saturday}. Here's how to reach us:`,
          true
        ),
        contactMsg(),
      ]);
      return;
    }
    if (intent === 'hours') {
      respond(() => [
        textMsg(
          `Our office hours are ${CONTACT.officeHours.weekdays} (${CONTACT.officeHours.timezone}); ${CONTACT.officeHours.saturday}. ${CONTACT.responsePromise} - and you can message us any time:`,
          true
        ),
        contactMsg(),
      ]);
      return;
    }

    // ── Ambiguous: offer the closest matches, always with a way out (H6) ────
    if (tier === 'suggest') {
      lastQueryRef.current = query;
      respond(() => [
        textMsg('Here are the closest matches - tap the one you mean:'),
        {
          id: genId(),
          kind: 'suggestions',
          items: results.map((r) => r.item),
          escape: true,
          ts: now(),
        },
      ]);
      return;
    }

    // ── Nothing on-topic: log the gap (audit suggestion 21), then help ──────
    track('chat_unmatched', { query: rawText.slice(0, 120) });
    if (looksNonEnglish(rawText)) {
      respond(() => [
        textMsg(
          "I can only answer in English for now, but our export team works with buyers across the UK and EU and can reply in your language. Email us or reach out below and we'll help:"
        ),
        contactMsg(),
      ]);
      return;
    }
    respond(() => [
      textMsg(
        "I'm not sure I have that in Flora Dine's FAQ. Our export team can give you a precise answer within 24–48 hours - here's how to reach them:"
      ),
      contactMsg(),
    ]);
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSend = (e: FormEvent) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text || isTyping) return;

    setShowTopics(false);
    append({ id: genId(), kind: 'text', role: 'user', content: text, ts: now() });
    setInputValue('');
    answerQuery(text);
  };

  const handleTopic = (topic: Topic) => {
    setShowTopics(false);
    append({ id: genId(), kind: 'text', role: 'user', content: topic.label, ts: now() });

    if (topic.seed === '__contact__') {
      sendContactCard(
        'Of course - our export & sales team is happy to help. Reach us on any of these channels:'
      );
      return;
    }
    lastQueryRef.current = topic.seed;
    answerQuery(topic.seed);
  };

  const handleSuggestion = (item: FAQItem) => {
    setShowTopics(false);
    append({ id: genId(), kind: 'text', role: 'user', content: item.question, ts: now() });
    lastQueryRef.current = item.question;
    respond(() => [textMsg(item.answer, true)]);
  };

  // ── Feedback (audit L2) ─────────────────────────────────────────────────────
  // Records a thumbs rating on an answer and forwards it to analytics so FAQ
  // gaps surface. A second tap on the same thumb clears it. GA4 is a no-op when
  // unconfigured, so this is safe everywhere.
  const rateMessage = (id: number, rating: 'up' | 'down') => {
    let next: 'up' | 'down' | undefined;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== id || m.kind !== 'text') return m;
        next = m.rating === rating ? undefined : rating;
        return { ...m, rating: next };
      })
    );
    if (next) track('chat_feedback', { rating: next });
  };

  // Build a plain-text transcript for the "Email this chat" mailto (audit L2).
  const transcriptHref = (): string => {
    const lines = messages
      .map((m) => {
        if (m.kind === 'text') return `${m.role === 'user' ? 'You' : 'Flora Dine'}: ${m.content}`;
        if (m.kind === 'suggestions')
          return `Flora Dine: [${m.items.map((i) => i.question).join(' / ')}]`;
        if (m.kind === 'contact') return `Flora Dine: [contact details shared]`;
        return '';
      })
      .filter(Boolean)
      .join('\n\n');
    const body = `My Flora Dine chat:\n\n${lines}`.slice(0, 1800);
    return `mailto:${CONTACT.email}?subject=${encodeURIComponent(
      'My Flora Dine chat'
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleReset = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setIsTyping(false);
    setConfirmReset(false);
    idRef.current = 2;
    lastQueryRef.current = '';
    setMessages(freshThread());
    setShowTopics(true);
    inputRef.current?.focus();
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderContactCard = () => (
    <div className="w-full space-y-2">
      <a
        href={`mailto:${CONTACT.email}`}
        className="group flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-muted"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon name="EnvelopeIcon" size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-foreground">Email our sales team</span>
          <span className="block truncate text-xs text-muted-foreground">{CONTACT.email}</span>
        </span>
        <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
      </a>

      {/* One row per line so each number dials itself (audit F7). The WhatsApp
          button is a sibling of the tel: link, never nested inside it. */}
      {PHONE_LINES.map((line) => (
        <div
          key={line.tel}
          className="flex items-center gap-1 rounded-xl border border-border bg-surface pr-1.5 transition-colors hover:border-primary/40 hover:bg-muted"
        >
          <a
            href={`tel:${line.tel}`}
            className="group flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon name="PhoneIcon" size={18} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">
                Call our {line.region} line
              </span>
              <span className="block truncate text-xs text-muted-foreground">{line.display}</span>
            </span>
          </a>
          {line.wa ? (
            <WhatsAppLink
              wa={line.wa}
              region={line.region}
              text={`Hi Flora Dine, I have an export enquiry. (${line.region} line)`}
              analyticsId={`chat_whatsapp_${line.region.toLowerCase()}`}
              size={18}
              className="bg-[#25D366]/10 hover:bg-[#25D366]/20"
            />
          ) : (
            <Icon name="ChevronRightIcon" size={16} className="mr-1.5 text-muted-foreground" />
          )}
        </div>
      ))}

      {/* The default WhatsApp path, kept prominent: the India line is the
          staffed one, so it stays the primary CTA even though both lines are
          individually reachable from the rows above. */}
      <a
        href={waUrl(CONTACT.whatsappIndia, 'Hi Flora Dine, I have an export enquiry.')}
        target="_blank"
        rel="noopener noreferrer"
        data-analytics-id="chat_whatsapp_primary"
        className="group flex items-center gap-3 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 px-3 py-2.5 transition-colors hover:bg-[#25D366]/10"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366]/15 text-[#128C7E]">
          <Icon name="WhatsApp" size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-foreground">Chat on WhatsApp</span>
          <span className="block truncate text-xs text-muted-foreground">
            Fastest response · India line {CONTACT.phoneIndia}
          </span>
        </span>
        <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
      </a>
    </div>
  );

  return (
    <div
      id="livechat-popup"
      role="dialog"
      aria-modal="false"
      aria-labelledby="livechat-title"
      className="flex h-136 max-h-[80vh] w-88 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-dramatic animate-slide-up"
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="flex items-center gap-3 bg-primary px-4 py-3 text-primary-foreground">
        <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 ring-1 ring-primary-foreground/20">
          <Icon name="SparklesIcon" size={20} className="text-primary-foreground" />
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-primary bg-success" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id="livechat-title" className="font-headline text-base font-semibold leading-tight">
            Flora Dine Support
          </h3>
          <p className="flex items-center gap-1.5 text-xs text-primary-foreground/80">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
            Automated assistant · answers instantly
          </p>
        </div>

        <button
          type="button"
          onClick={() => setConfirmReset(true)}
          aria-label="Start a new conversation"
          title="New conversation"
          className="rounded-lg p-1.5 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/15 hover:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50"
        >
          <Icon name="ArrowPathIcon" size={18} className="text-primary-foreground" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="rounded-lg p-1.5 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/15 hover:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50"
        >
          <Icon name="XMarkIcon" size={20} className="text-primary-foreground" />
        </button>
      </header>

      {/* ── Reset confirmation (audit L3: don't wipe history on a single tap) ── */}
      {confirmReset && (
        <div className="flex items-center gap-2 border-b border-border bg-muted px-4 py-2.5 text-sm">
          <span className="flex-1 text-foreground">Clear this conversation?</span>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setConfirmReset(false)}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Messages ────────────────────────────────────────────────────── */}
      <div
        ref={scrollAreaRef}
        className="flex-1 space-y-3 overflow-y-auto px-3 py-4"
        role="log"
        aria-live="polite"
        aria-atomic="false"
      >
        {messages.map((message) => {
          if (message.kind === 'suggestions') {
            return (
              <div key={message.id} className="flex flex-col gap-1.5 pl-9">
                {message.items.map((faq) => (
                  <button
                    key={faq.question}
                    onClick={() => handleSuggestion(faq)}
                    className="group flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-primary/40 hover:bg-muted"
                  >
                    <Icon name="MagnifyingGlassIcon" size={15} className="shrink-0 text-primary" />
                    <span className="flex-1">{faq.question}</span>
                    <Icon
                      name="ChevronRightIcon"
                      size={14}
                      className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                    />
                  </button>
                ))}
                {/* Always a way out of a shortlist, straight to a human (H6). */}
                {message.escape && (
                  <button
                    onClick={() =>
                      sendContactCard(
                        "No problem - here's how to reach our export & sales team directly:"
                      )
                    }
                    className="group flex items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={15} className="shrink-0" />
                    <span className="flex-1">None of these - talk to sales</span>
                    <Icon
                      name="ChevronRightIcon"
                      size={14}
                      className="shrink-0 transition-transform group-hover:translate-x-0.5"
                    />
                  </button>
                )}
              </div>
            );
          }

          if (message.kind === 'actions') {
            return (
              <div key={message.id} className="flex flex-col gap-1.5 pl-9">
                {message.items.map((action) => (
                  <Link
                    key={action.href}
                    href={action.href}
                    className="group flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-left text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-muted"
                  >
                    <Icon name={action.icon} size={15} className="shrink-0 text-primary" />
                    <span className="flex-1">{action.label}</span>
                    <Icon
                      name="ArrowRightIcon"
                      size={14}
                      className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                ))}
              </div>
            );
          }

          if (message.kind === 'contact') {
            return (
              <div key={message.id} className="pl-9">
                {renderContactCard()}
              </div>
            );
          }

          // text message
          const isUser = message.role === 'user';
          return (
            <div
              key={message.id}
              className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              {!isUser && (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center self-end rounded-full bg-primary/10 text-primary">
                  <Icon name="SparklesIcon" size={14} className="text-primary" />
                </span>
              )}
              <div className={`flex max-w-[78%] flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                <div
                  className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-subtle ${
                    isUser
                      ? 'rounded-br-sm bg-primary text-primary-foreground'
                      : 'rounded-bl-sm border border-border bg-surface text-foreground'
                  }`}
                >
                  <p className="whitespace-pre-line">{message.content}</p>
                  <span
                    className={`mt-1 block text-[10px] ${
                      isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}
                  >
                    {formatTime(message.ts)}
                  </span>
                </div>

                {/* Answer feedback (audit L2) */}
                {message.kind === 'text' && message.feedback && (
                  <div className="mt-1 flex items-center gap-1.5 pl-1 text-muted-foreground">
                    {message.rating ? (
                      <span className="text-[11px]">
                        {message.rating === 'up'
                          ? 'Thanks for the feedback 🌱'
                          : 'Thanks - tap Talk to Sales below for a human.'}
                      </span>
                    ) : (
                      <>
                        <span className="text-[11px]">Was this helpful?</span>
                        <button
                          type="button"
                          onClick={() => rateMessage(message.id, 'up')}
                          aria-label="Helpful"
                          className="rounded-md p-1 transition-colors hover:bg-muted hover:text-success focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                        >
                          <Icon name="HandThumbUpIcon" size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => rateMessage(message.id, 'down')}
                          aria-label="Not helpful"
                          className="rounded-md p-1 transition-colors hover:bg-muted hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                        >
                          <Icon name="HandThumbDownIcon" size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Quick-topic launcher */}
        {showTopics && !isTyping && (
          <div className="pl-9">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Popular topics</p>
            <div className="grid grid-cols-2 gap-1.5">
              {TOPICS.map((topic) => (
                <button
                  key={topic.label}
                  onClick={() => handleTopic(topic)}
                  className="group flex items-center gap-2 rounded-xl border border-border bg-surface px-2.5 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-muted"
                >
                  <Icon name={topic.icon} size={16} className="shrink-0 text-primary" />
                  <span className="leading-tight">{topic.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex items-end gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon name="SparklesIcon" size={14} className="text-primary" />
            </span>
            <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-border bg-surface px-3.5 py-3">
              <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" />
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60"
                style={{ animationDelay: '0.15s' }}
              />
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60"
                style={{ animationDelay: '0.3s' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Composer ────────────────────────────────────────────────────── */}
      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 border-t border-border bg-surface px-3 py-2.5"
      >
        <label htmlFor="livechat-input" className="sr-only">
          Type your message
        </label>
        <input
          ref={inputRef}
          id="livechat-input"
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Ask about products, pricing, shipping…"
          autoComplete="off"
          className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <button
          type="submit"
          aria-label="Send message"
          disabled={!inputValue.trim() || isTyping}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition hover:bg-primary/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
        >
          <Icon name="PaperAirplaneIcon" size={18} className="text-primary-foreground" />
        </button>
      </form>

      <div className="flex items-center justify-center gap-2 bg-surface pb-2 text-[10px] text-muted-foreground">
        <span>Powered by Flora Dine · We never share your details</span>
        {messages.length > 1 && (
          <>
            <span aria-hidden="true">·</span>
            <a
              href={transcriptHref()}
              className="inline-flex items-center gap-1 font-medium text-primary transition-colors hover:underline"
            >
              <Icon name="EnvelopeIcon" size={11} />
              Email this chat
            </a>
          </>
        )}
      </div>
    </div>
  );
};
